import React, {
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  ComponentType,
} from 'react'
import { pick, sortBy, find } from 'lodash/fp'
import cn from 'classnames'
import useDebounce from 'react-use/lib/useDebounce'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import useThunkDispatch from '../../common/useThunkDispatch'
import Search from '../../components/Search'
import Button from '../../components/Button'
import GridLayout from '../../components/GridLayout'
import { IHash } from '../../types/common'
import { IBranch, IAccount, IUser } from '../../types/user'
import { IProduct } from '../../types/products'
import Splash from '../../components/Splash'
import ImportDialog from '../../components/ImportDialog'
import { uploadProducts } from '../../accounts/accountsActions'
import withAccountAndBranch from '../../common/withAccountAndBranch'
import useClientTitle from '../../common/useClientTitle'
import { useProductAddModal } from './ProductAdd'
import ProductCard from './ProductCard'
import ProductInfo from './ProductInfo'
import { IHistoryReturnState } from './InventoryEvents'
import { useLocation } from 'react-router-dom'
import {
  requestInventory,
  requestProductCurrentInventory,
  requestProductUpsert,
  requestProductRemove,
  requestTagUpdate,
  requestTagDelete,
  IInventoryList,
  ITagFilter,
} from './inventoryActions'
import TagEditor from './TagEditor'
import TagEditorModal from './TagEditorModal'
import TagDot from './TagDot'
import PencilIcon from '../../icons/pencil.svg'
import { SimpleIndex } from '../../common/simpleSearch'
import { getShouldRenderTruckInventoryFeature } from '../../common/getShouldRenderTruckInventoryFeature'
import { getShouldRenderBinInventoryFeature } from '../../common/getShouldRenderBinInventoryFeature'

interface IFilter {
  title: string
  options: Array<{
    key: string
    label: string
    className?: string | undefined
  }>
}

const makeFilters = (list: IInventoryList): IFilter[] => [
  {
    title: 'Products',
    options: [
      {
        key: 'all',
        label: 'All',
      },
      {
        key: 'unallocated',
        label: 'Unallocated Only',
      },
      {
        key: 'lowstock',
        label: 'Low Stock',
      },
      ...list.divisions.map((d, idx) => ({
        key: `${d.name}-${d.id}`,
        label: d.name,
        className: idx === 0 ? '-separated' : undefined,
      })),
    ],
  },
]

const Row: ComponentType<ListChildComponentProps> = ({
  data,
  index,
  style,
}) => {
  const p = data.products[index]
  return (
    <div style={style} key={p.id}>
      <ProductCard
        product={p}
        className={cn({
          '-selected': data.product && data.product.id === p.id,
        })}
        onClick={() => data.onClick(p)}
        multiCatalogEnabled={data.multiCatalogEnabled}
      />
    </div>
  )
}

const Inventory: FC<{
  branch: IBranch
  account?: IAccount
  isInternal: boolean
  user: IUser
}> = ({ branch, account, isInternal, user }) => {
  const dispatch = useThunkDispatch()
  const [errors, setErrors] = useState<IHash<string>>({})
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [importProductsOpen, setImportProductsOpen] = useState<boolean>(false)
  const [term, setTerm] = useState<string | undefined>('')
  const [list, setList] = useState<IInventoryList | undefined>(undefined)
  const [baseList, setBaseList] = useState<IInventoryList | undefined>(
    undefined,
  )
  const [filters, setFilters] = useState<IFilter[]>([])
  const [filter, setFilter] = useState<string>('all')
  const [tagFilter, setTagFilter] = useState<ITagFilter | undefined>(undefined)
  const [products, setProducts] = useState<IProduct[]>([])
  const [product, setProduct] = useState<IProduct | undefined>(undefined)
  const [distributors, setDistributors] = useState<string[]>([])
  const [editingTagId, setEditingTagId] = useState<number | null>(null)
  const inventoyList = useRef<HTMLInputElement>(null)
  const prevFilterRef = useRef(filter)
  // product to reopen once, when returning from an order opened in its history
  const returnProductIdRef = useRef(
    (useLocation().state as IHistoryReturnState | null)?.productId,
  )

  // common
  useClientTitle({ branch, account })

  // search
  const productsIndex = useMemo(() => {
    const allProducts = list ? list.products : undefined
    return new SimpleIndex<IProduct>(allProducts ? allProducts[filter] : [], {
      keys: ['name', 'code'],
    })
  }, [list, filter])

  // scroll
  const scrollTop = () => {
    if (!inventoyList.current) return
    inventoyList.current.scrollIntoView({ behavior: 'smooth' })
  }

  // set products sorted
  const selectProducts = useCallback((products: IProduct[]) => {
    setProducts(sortBy((p) => p.code.toLowerCase(), products))
  }, [])

  // fetch/refetch catalog
  const loadInventory = async (branch: IBranch, tf?: ITagFilter) => {
    const list = await dispatch(
      requestInventory(
        branch.id,
        account && account.id,
        truckInventoryFeature || binInventoryFeature,
        tf,
      ),
    )
    setList(list)
    if (tf) setFilter('all')
    else setBaseList(list)
    setFilters(makeFilters(list))
    setDistributors(list.distributors)
  }

  const erpCatalogs = account?.erpCatalogs || user.account?.erpCatalogs || []
  const multiCatalogEnabled = erpCatalogs.length > 1

  const truckInventoryFeature = getShouldRenderTruckInventoryFeature(
    account?.truckInventoryFeature ?? user.account?.truckInventoryFeature,
    branch?.truckInventoryFeature,
  )

  const binInventoryFeature = getShouldRenderBinInventoryFeature(
    account?.binInventoryFeature ?? user.account?.binInventoryFeature,
    branch?.binInventoryFeature,
  )

  const templateName = multiCatalogEnabled
    ? 'import_division_products_with_source_template.csv'
    : 'import_division_products_template.csv'
  const templateUrl = multiCatalogEnabled
    ? '/import_division_products_with_source_template.csv'
    : '/import_division_products_template.csv'

  // on create new
  const handleCreateNew = useCallback(
    ({ name, code }: { name?: string; code?: string }) => {
      setErrors({})
      setProduct({
        code: code || '',
        name: name || '',
        price: '',
        unit: '',
        inventory: undefined,
        distributor: null,
        quantity: 1,
        divisions: [],
        custom: true,
      } as any)
    },
    [],
  )

  // on use based on product
  const handleUseProduct = useCallback((product: IProduct) => {
    setErrors({})
    setProduct({
      ...pick(['id', 'code', 'name', 'price', 'unit', 'distributor'], product),
      divisions: [],
      quantity: 1,
      custom: true,
      isAddingToBranch: true,
    } as any)
  }, [])

  // product add modal
  const [{ open: handleProductAdd }, addProductModal] = useProductAddModal({
    branch,
    account,
    existsProducts: products,
    onCreate: handleCreateNew,
    onUse: handleUseProduct,
  })

  // handle product save
  const handleSave = async (product: IProduct) => {
    const perform = async () => {
      try {
        setErrors({})
        setSubmitting(true)
        const { result, errors } = await dispatch(
          requestProductUpsert(product, branch!),
        )
        if (errors) {
          setErrors(errors)
          scrollTop()
        } else {
          setProduct((prev) =>
            prev ? { ...prev, ...result, pendingNewTag: undefined } : result,
          )
          await loadInventory(branch!, tagFilter)
        }
      } finally {
        setSubmitting(false)
      }
    }

    if (branch) {
      await perform()
    }
  }

  // handle product remove
  const handleRemove = useCallback(
    (product: IProduct) => {
      const perform = async () => {
        try {
          setErrors({})
          setSubmitting(true)
          const errors = await dispatch(requestProductRemove(product, branch!))
          if (!errors) {
            setProduct(undefined)
            loadInventory(branch!, tagFilter)
          } else {
            setErrors(errors)
            if (errors.base) alert(errors.base)
          }
        } finally {
          setSubmitting(false)
        }
      }

      if (branch) {
        perform()
      }
    },
    [branch, filter],
  )

  // initial load
  useEffect(() => {
    if (branch) {
      loadInventory(branch, tagFilter)
    }
  }, [branch])

  const handleTagFilterChange = (tf: ITagFilter | undefined) => {
    setTagFilter(tf)
    if (branch) loadInventory(branch, tf)
  }

  const handleDivisionFilterChange = (key: string) => {
    setFilter(key)
    if (tagFilter !== undefined) {
      setTagFilter(undefined)
      if (branch) loadInventory(branch, undefined)
    }
  }

  const handleTagUpdate = async (id: number, name: string, color: string) => {
    const result = await dispatch(requestTagUpdate(id, name, color))
    if (!result.errors && branch) {
      setEditingTagId(null)
      await loadInventory(branch, tagFilter)
    }
    return result
  }

  const handleTagDelete = async (id: number) => {
    const result = (await dispatch(requestTagDelete(id))) ?? {}
    if (result.errors) return
    setEditingTagId(null)
    handleTagFilterChange(undefined)
  }

  const patchProductInList = useCallback(
    (productId: number, patch: Partial<IProduct>) => {
      setList((prev) => {
        if (!prev) return prev
        const applyPatch = (arr: IProduct[]) =>
          arr.map((item) =>
            item.id === productId ? { ...item, ...patch } : item,
          )
        return {
          ...prev,
          products: Object.fromEntries(
            Object.entries(prev.products).map(([key, arr]) => [
              key,
              applyPatch(arr as IProduct[]),
            ]),
          ),
        }
      })
    },
    [],
  )

  const syncProductInventory = useCallback(
    async (targetProduct: IProduct) => {
      const fresh = await dispatch(
        requestProductCurrentInventory(
          targetProduct.id,
          branch!.id,
          account?.id,
        ),
      )
      if (!fresh) return
      patchProductInList(targetProduct.id, fresh)
    },
    [branch, account, dispatch, patchProductInList],
  )

  useEffect(() => {
    if (!list) return
    const filterChanged = prevFilterRef.current !== filter
    prevFilterRef.current = filter

    if (!filterChanged) {
      const productId = product?.id ?? returnProductIdRef.current
      returnProductIdRef.current = undefined
      const updatedProduct = productId
        ? find({ id: productId }, list.products.all)
        : undefined
      setProduct(updatedProduct)
    }

    researchData()
    if (filterChanged) scrollTop()
  }, [list, filter])

  const researchData = () => {
    if (list) {
      const results = term
        ? productsIndex.search(term || '')
        : list.products[filter]

      selectProducts(results)
    }
  }

  // on search
  useDebounce(() => researchData(), 300, [term])

  if (!branch) return <Splash isLoading={true} />

  const pageLoading = !list

  return (
    <div ref={inventoyList} className="list -scrollable splash_wrapper">
      <div className="filter">
        <div className="filter_col -large">
          <Search
            placeholder="Search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
        <div className="filter_sep" />
        <div className="filter_col -flex">
          <Button
            type="submit"
            caliber="sm"
            className="list_action"
            onClick={() => {
              setProduct(undefined)
              handleProductAdd()
            }}
          >
            Add Product
          </Button>
          {isInternal && (
            <Button
              type="submit"
              caliber="sm"
              className="list_action"
              onClick={() => {
                if (
                  list?.hasInvoices === false &&
                  !window.confirm(
                    'No invoices found. Products may be miscategorized. Continue?',
                  )
                )
                  return
                setImportProductsOpen(true)
              }}
            >
              Import
            </Button>
          )}
        </div>
      </div>
      <GridLayout className="list_grid -wide" offset={185} scrollable>
        <div>
          {filters.map((f) => (
            <div key={f.title} className="list_filter">
              <div className="list_filter-title">{f.title}</div>
              {f.options.map((option) => (
                <div
                  key={option.key}
                  className={cn('list_filter-row', option.className)}
                  onClick={() => handleDivisionFilterChange(option.key)}
                >
                  <span
                    className={cn('list_filter-value', {
                      '-selected': option.key === filter,
                    })}
                  >
                    {option.label}
                  </span>
                  {(baseList?.products[option.key] || []).length}
                </div>
              ))}
            </div>
          ))}
          {list && list.productTags.length > 0 && (
            <>
              {editingTagId !== null &&
                (() => {
                  const editingTag = list.productTags.find(
                    (t) => Number(t.id) === editingTagId,
                  )
                  return editingTag ? (
                    <TagEditorModal
                      title="Edit tag"
                      onClose={() => setEditingTagId(null)}
                    >
                      <TagEditor
                        mode="edit"
                        tag={editingTag}
                        onSave={(name, color) =>
                          handleTagUpdate(Number(editingTag.id), name, color)
                        }
                        onDelete={() => handleTagDelete(Number(editingTag.id))}
                      />
                    </TagEditorModal>
                  ) : null
                })()}
              <div className="list_filter">
                <div className="list_filter-title">TAGS</div>
                <div
                  className="list_filter-row"
                  onClick={() => handleTagFilterChange({ untaggedOnly: true })}
                >
                  <span
                    className={cn('list_filter-value -tag', {
                      '-selected': tagFilter?.untaggedOnly,
                    })}
                  >
                    <TagDot />
                    No tag
                  </span>
                  <span className="list_filter-tag-count">
                    {list.untaggedProductsCount}
                  </span>
                </div>
                {list.productTags.map((tag) => (
                  <div
                    key={tag.id}
                    className="list_filter-row -tag-row"
                    onClick={() =>
                      handleTagFilterChange({ productTagId: Number(tag.id) })
                    }
                  >
                    <span
                      className={cn('list_filter-value -tag', {
                        '-selected': tagFilter?.productTagId === Number(tag.id),
                      })}
                    >
                      <TagDot color={tag.color} />
                      {tag.name}
                    </span>
                    <span className="list_filter-tag-count">
                      {tag.productsCount}
                    </span>
                    <button
                      type="button"
                      className="list_filter-tag-edit"
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditingTagId(Number(tag.id))
                      }}
                    >
                      <PencilIcon />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={{ height: `100%` }}>
          <div className="list_items" style={{ height: `100%` }}>
            {!pageLoading && !products.length && (
              <div className="list_placeholder">No products found</div>
            )}
            {!!products.length && (
              <AutoSizer>
                {(size) => (
                  <FixedSizeList
                    {...size}
                    itemData={{
                      products,
                      product,
                      onClick: (p: IProduct) => {
                        setErrors({})
                        setProduct(p)
                        syncProductInventory(p)
                      },
                      multiCatalogEnabled,
                    }}
                    itemCount={products.length}
                    itemSize={102 + 5}
                    itemKey={(index) => products[index].id}
                  >
                    {Row}
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
          </div>
        </div>
        <div>
          {!pageLoading && !product && (
            <div className="list_placeholder">Select product for details</div>
          )}
          {product && (
            <ProductInfo
              product={product}
              branch={branch}
              account={account}
              loading={submitting}
              divisions={list?.divisions || []}
              distributors={distributors}
              errors={errors}
              truckInventoryFeature={truckInventoryFeature}
              binInventoryFeature={binInventoryFeature}
              productTags={list?.productTags || []}
              onSave={handleSave}
              onRemove={handleRemove}
              onRefetch={() => loadInventory(branch!, tagFilter)}
              onSyncProduct={() => product && syncProductInventory(product)}
              onProductInventoryUpdate={(patch) =>
                product && patchProductInList(product.id, patch)
              }
              onCancel={() => {
                setErrors({})
                setProduct(undefined)
              }}
            />
          )}
        </div>
      </GridLayout>
      {addProductModal}
      {importProductsOpen && (
        <ImportDialog
          close={(status) => {
            setImportProductsOpen(false)
            if (status === 'done') loadInventory(branch, tagFilter)
          }}
          action={(file: File) => uploadProducts(file, branch.id)}
          templateName={templateName}
          templateUrl={templateUrl}
          title="Import Products"
        />
      )}
      <Splash isLoading={pageLoading} bgColor="rgba(242, 246, 250, 0.72)" />
    </div>
  )
}

export default withAccountAndBranch(Inventory)
